import { FocusEvent, KeyboardEvent, useId, useRef, useState } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { Dropdown, Text } from '../Ant';
import { ChevronDown } from '../icons';
import SetupField from '../setupField/setupField';
import StartModulePanel from './startModulePanel';
import { usePopupFocus } from '../../hooks/usePopupFocus';
import { IFeature, StartModule } from '../../interface/featureSelection';
import { PickerTrigger } from '../../assets/styles/components/featureBox';

// The list is never scrolled, so on a short pane the panel slides into view instead of running off the edge.
const KEEP_PANEL_IN_PANE = { overflow: { adjustX: true, adjustY: true, shiftY: true } };

interface IStartModuleSelectProps {
  /** The features that are switched on; only these can be the start module. */
  options: IFeature[];
  value: StartModule;
  onChange: (startModule: StartModule) => void;
  /** `labelled` is the workspace setup field; `plain` is the bare select used inside a preference row. */
  variant?: 'labelled' | 'plain';
}

const StartModuleSelect = ({
  options,
  value,
  onChange,
  variant = 'labelled',
}: IStartModuleSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const listboxId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = () => setOpen(false);
  const { contentRef, onKeyDown, onBlur, tabIndex } = usePopupFocus<HTMLDivElement>(open, close);

  const label = t('common:features.startModule');
  const selectedFeature = options.find((option) => option.id === value);
  const selectedText = selectedFeature?.text ?? '';

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown' || open) return;
    event.preventDefault();
    setOpen(true);
  };

  /** Focus moving to the trigger is a click on it, which closes the panel itself; closing here too would reopen it. */
  const handlePanelBlur = (event: FocusEvent<HTMLElement>) => {
    if (event.relatedTarget === triggerRef.current) return;
    onBlur(event);
  };

  const handleSelect = (startModule: StartModule) => {
    onChange(startModule);
    close();
  };

  const renderStartModulePanel = () => (
    <StartModulePanel
      ref={contentRef}
      rootProps={{ onKeyDown, onBlur: handlePanelBlur, tabIndex }}
      listboxId={listboxId}
      options={options}
      value={value}
      onSelect={handleSelect}
      ariaLabel={label}
    />
  );

  const triggerProps = {
    ref: triggerRef,
    'aria-label': `${label}: ${selectedText}`,
    'aria-haspopup': 'listbox' as const,
    'aria-controls': open ? listboxId : undefined,
    onKeyDown: handleTriggerKeyDown,
    'data-testid': 'start-module-select',
  };

  return (
    <Dropdown
      open={open}
      trigger={['click']}
      placement={variant === 'labelled' ? 'topLeft' : 'bottomLeft'}
      align={KEEP_PANEL_IN_PANE}
      destroyOnHidden
      getPopupContainer={() => document.body}
      onOpenChange={setOpen}
      popupRender={renderStartModulePanel}
    >
      {variant === 'labelled' ? (
        <SetupField
          label={label}
          icon={selectedFeature?.icon}
          value={selectedText}
          isOpen={open}
          {...triggerProps}
        />
      ) : (
        <PickerTrigger
          type="button"
          aria-expanded={open}
          className="w-full p-10 ta-left cursor-pointer"
          {...triggerProps}
        >
          <Flex component="span" align="center" gap={5} className="w-full">
            {selectedFeature?.icon}
            <Text
              $fontsize="13px"
              $weight={500}
              $lineheight="18px"
              $color="title"
              $width="auto"
              className="flex-grow min-w-0"
            >
              {selectedText}
            </Text>
            <ChevronDown className="picker-chevron fz-18 flex-shrink-0 text-clr-general-description" />
          </Flex>
        </PickerTrigger>
      )}
    </Dropdown>
  );
};

export default StartModuleSelect;
